import React from "react";
import { ChevronDown, Play, MapPin } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { useContent } from "../context/ContentContext";

const Hero = () => {
  const { player, gallery } = useContent();
  return (
    <section
      id="home"
      className="relative min-h-screen w-full overflow-hidden bg-neutral-950 pt-24 md:pt-28"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 field-lines opacity-60" />
      <div className="absolute -top-40 -right-20 w-[520px] h-[520px] rounded-full bg-red-700/20 blur-[120px]" />
      <div className="absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full bg-red-900/20 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-10 pb-20 md:pb-28 grid lg:grid-cols-12 gap-10 items-center">
        {/* Text */}
        <div className="lg:col-span-7 rise-in">
          <div className="flex items-center gap-3 mb-6">
            <span className="accent-bar" />
            <span className="text-xs uppercase tracking-[0.35em] text-neutral-400 font-heading">
              Defensive Line · Two-Way Lineman
            </span>
          </div>

          <h1 className="font-display text-white leading-[0.88] text-6xl sm:text-7xl md:text-8xl lg:text-[8.5rem]">
            {player.firstName.toUpperCase()}
            <br />
            <span className="text-red-600">{player.lastName.toUpperCase()}</span>
          </h1>

          <p className="mt-6 text-lg md:text-xl text-neutral-300 max-w-2xl">
            {player.tagline}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Badge className="bg-red-600 hover:bg-red-700 text-white font-heading uppercase tracking-widest rounded-sm px-3 py-1">
              Class of {player.classOf}
            </Badge>
            <Badge className="bg-neutral-800 hover:bg-neutral-700 text-neutral-100 font-heading uppercase tracking-widest rounded-sm px-3 py-1">
              DE / DT / OT
            </Badge>
            <Badge className="bg-neutral-800 hover:bg-neutral-700 text-neutral-100 font-heading uppercase tracking-widest rounded-sm px-3 py-1">
              #{player.jerseyNumber}
            </Badge>
            <Badge className="bg-transparent border border-neutral-700 text-neutral-200 font-heading uppercase tracking-widest rounded-sm px-3 py-1">
              <MapPin className="w-3 h-3 mr-1" /> Fishers, IN
            </Badge>
          </div>

          <div className="mt-10 flex flex-wrap gap-4">
            <Button
              asChild
              className="btn-primary bg-red-600 hover:bg-red-700 text-white font-heading uppercase tracking-widest rounded-sm px-7 h-12"
            >
              <a href="#film">
                <Play className="w-4 h-4 mr-2" /> Watch Film
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              className="btn-primary bg-transparent border-neutral-600 text-white hover:bg-neutral-900 hover:text-white font-heading uppercase tracking-widest rounded-sm px-7 h-12"
            >
              <a href="#contact">Contact Coach</a>
            </Button>
          </div>

          {/* Quick measurables */}
          <div className="mt-12 grid grid-cols-3 gap-4 max-w-xl">
            {[
              { label: "Height", value: player.heightCurrent },
              { label: "Weight", value: player.weight },
              { label: "Projected", value: player.heightProjected },
            ].map((m) => (
              <div
                key={m.label}
                className="border border-neutral-800 bg-neutral-900/40 rounded-sm p-4"
              >
                <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                  {m.label}
                </div>
                <div className="mt-1 font-display text-2xl text-white">
                  {m.value}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Image / Jersey frame */}
        <div className="lg:col-span-5 relative">
          <div className="relative rise-in">
            <div className="absolute -inset-3 border border-red-600/40 rounded-sm pointer-events-none" />
            <div className="absolute -top-6 -left-6 font-display text-[10rem] md:text-[14rem] leading-none jersey-number select-none pointer-events-none">
              20
            </div>
            <div className="relative overflow-hidden rounded-sm border border-neutral-800 bg-neutral-900">
              <img
                src={gallery[0].src}
                alt="DaMarkas McNeese"
                className="w-full h-[520px] md:h-[620px] object-cover img-reveal"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-heading">
                    Fishers, IN
                  </div>
                  <div className="font-display text-2xl text-white">
                    {player.school}
                  </div>
                </div>
                <div className="font-display text-5xl text-white">
                  #{player.jerseyNumber}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <a
        href="#about"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-neutral-500 hover:text-red-500 transition-colors"
        aria-label="Scroll to about"
      >
        <ChevronDown className="w-8 h-8 animate-bounce" />
      </a>
    </section>
  );
};

export default Hero;
